import Container from "@mui/material/Container";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { memo, useMemo } from "react";

import SelectLanguage from "@/components/Core/SelectLanguage";
import { getMediaUrl, isUnbuiltRoute } from "@/configs";
import { useTranslate } from "@/hooks";
import { openCookieSettings } from "@/lib/cookieConsent";
import { useSelector } from "@/store";

import { BrandSection } from "./BrandSection";
import Copyright from "./Copyright";
import { FooterLinks } from "./FooterLinks";
import { FooterColumn } from "./LinkSection";
import { MascotGreeter } from "./MascotGreeter";
import { FooterBar, FooterMain, FooterRoot, PartnerRow } from "./styles";

const Footer = memo(() => {
  const translate = useTranslate();
  const bootstrap = useSelector((state) => state.app.bootstrap);

  const footer = bootstrap?.footer;
  const description = bootstrap?.data?.desc || "";

  const columns = useMemo<FooterColumn[]>(() => {
    if (!footer) return [];
    return footer.lists
      .filter((list) => list.status === "active")
      .map((list) => ({
        id: list._id,
        title: list.name,
        items: footer.items
          .filter(
            (item) =>
              item.status === "active" &&
              String(item.pid) === String(list._id) &&
              !isUnbuiltRoute(item.url),
          )
          .map((item) => ({ id: item._id, title: item.name, link: item.url })),
      }));
  }, [footer]);

  const images = useMemo(
    () => (footer?.images ?? []).filter((image) => image.status === "active"),
    [footer],
  );

  return (
    <FooterRoot>
      <Container maxWidth={false}>
        <MascotGreeter />

        <FooterMain>
          <BrandSection description={description} />
          {columns.length > 0 && <FooterLinks sections={columns} />}
        </FooterMain>

        {images.length > 0 && (
          <PartnerRow>
            {images.map((image) => (
              <img key={image._id} src={getMediaUrl(image.url, 120)} loading="lazy" alt="" />
            ))}
          </PartnerRow>
        )}

        <FooterBar>
          <Stack
            direction={{ xs: "column", md: "row" }}
            sx={{ alignItems: "center", gap: { xs: 0.5, md: 2 } }}
          >
            <Copyright />
            <Typography variant="body2" color="text.secondary">
              {translate("mascot.footnote")}
            </Typography>
            <Link
              component="button"
              type="button"
              variant="body2"
              color="text.secondary"
              underline="hover"
              onClick={openCookieSettings}
            >
              {translate("footer.cookie-settings")}
            </Link>
          </Stack>
          <SelectLanguage />
        </FooterBar>
      </Container>
    </FooterRoot>
  );
});
Footer.displayName = "Footer";

export default Footer;
